import type { CreatePatientInput, PatientDemographics } from '@/api/patientsApi'
import { todayCalendarDate } from '@/lib/patient'

/**
 * The demographic fields a user keys, shared by the create page and the
 * correction dialog (z8nrz7dp78 AC2.6: same fields, same rules, same messages).
 * Kept free of React so the rules are unit-tested.
 */

/** The fields a user may key or correct, in form order. MRN is not one of them. */
export const DEMOGRAPHIC_FORM_FIELDS = [
  'firstName',
  'lastName',
  'sex',
  'birthDate',
  'preferredLanguage',
  'email',
  'phone',
  'addressLine',
  'city',
  'state',
  'postalCode',
  'country',
] as const

export type DemographicFormField = (typeof DEMOGRAPHIC_FORM_FIELDS)[number]

/** Every field as the inputs hold it: a string, blank when absent. */
export type DemographicsForm = Record<DemographicFormField, string>

export const EMPTY_DEMOGRAPHICS: DemographicsForm = {
  firstName: '',
  lastName: '',
  sex: '',
  birthDate: '',
  preferredLanguage: '',
  email: '',
  phone: '',
  addressLine: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
}

export interface FormOption {
  value: string
  label: string
}

export const SEX_OPTIONS: FormOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
]

/**
 * Stored value is the ISO 639-1 code (what the backend keeps). English, Spanish,
 * and French are pinned to the top as the most common; the rest are ordered by
 * global speaker count.
 */
export const LANGUAGES: FormOption[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'zh', label: 'Chinese (Mandarin)' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ar', label: 'Arabic' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'bn', label: 'Bengali' },
  { value: 'ru', label: 'Russian' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'vi', label: 'Vietnamese' },
]

/**
 * The options, plus the stored value when it is not one of them. A value the EHR
 * sent (`M`, `English`) must still show as selected, or opening the form would
 * blank it and a save would clear it without anyone choosing to.
 */
export function optionsWith(options: FormOption[], current: string): FormOption[] {
  if (!current || options.some(o => o.value === current)) return options
  return [...options, { value: current, label: current }]
}

export const BIRTH_DATE_FUTURE_MESSAGE = 'Date of birth cannot be in the future.'

/**
 * The date-of-birth rule (z8nrz7dp77 AC9). Blank is allowed: a date of birth
 * is never made mandatory. The input's `YYYY-MM-DD` compares as a string.
 */
export function birthDateError(value: string, today = todayCalendarDate()): string | null {
  return value && value > today ? BIRTH_DATE_FUTURE_MESSAGE : null
}

export const EMAIL_INVALID_MESSAGE = 'Enter a valid email address.'

/**
 * A loose email shape check, for a value the user typed. The server's @IsEmail is
 * the rule; this only catches a typo before the password prompt. Blank is allowed.
 */
export function emailError(value: string): string | null {
  const trimmed = value.trim()
  return trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? EMAIL_INVALID_MESSAGE : null
}

/**
 * A stored patient as the form holds it. The date of birth is its own leading
 * `YYYY-MM-DD`, never re-read through a `Date` (z8nrz7dp77).
 */
export function demographicsFormFrom(patient: Partial<PatientDemographics>): DemographicsForm {
  const form = { ...EMPTY_DEMOGRAPHICS }
  for (const field of DEMOGRAPHIC_FORM_FIELDS) {
    const value = patient[field]
    form[field] = field === 'birthDate' ? (value ?? '').slice(0, 10) : (value ?? '')
  }
  return form
}

/**
 * A create payload. Blank fields are dropped rather than sent as `''`: the
 * server validates `email` and `birthDate`, so an empty string would be refused
 * outright rather than read as "not provided".
 */
export function toCreatePayload(mrn: string, form: DemographicsForm): CreatePatientInput {
  const payload: Record<string, string> = { mrn: mrn.trim() }
  for (const field of DEMOGRAPHIC_FORM_FIELDS) {
    const trimmed = form[field].trim()
    if (trimmed) payload[field] = trimmed
  }
  return payload as unknown as CreatePatientInput
}

/** Only the fields a correction changes; a cleared field is `null`. */
export type DemographicsChanges = Partial<Record<DemographicFormField, string | null>>

/**
 * What a correction changes, compared trimmed. Empty when nothing changed, which
 * the dialog treats as a no-op: no signature prompt, nothing sent (AC2.5).
 */
export function demographicsChanges(
  original: DemographicsForm,
  form: DemographicsForm,
): DemographicsChanges {
  const changes: DemographicsChanges = {}
  for (const field of DEMOGRAPHIC_FORM_FIELDS) {
    const before = original[field].trim()
    const after = form[field].trim()
    if (before !== after) changes[field] = after || null
  }
  return changes
}
